import { createStore, combineReducers, applyMiddleware, compose } from 'redux';
import { routerMiddleware } from 'react-router-redux';
import { leanReducer } from 'lean-redux';
import { createLogger } from 'redux-logger';
import { thunk } from 'redux-thunk';
import { REDUX_LOGGING_ENABLED } from 'legacy-src/constants/config';
import { all } from 'redux-saga/effects';
import { routinePromiseWatcherSaga } from 'redux-saga-routines';
import createSagaMiddleware from 'redux-saga';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import hardSet from 'redux-persist/lib/stateReconciler/hardSet';

/* reducers */
import wizards from 'legacy-packages/wizards/src/reducers';
import { reducer as formReducer } from 'redux-form';
import apstudent from 'legacy/src/components/apstudent/handlers/state/reducer';
import assessment from 'legacy/src/components/assessment/state/reducer';
import assignItem from 'legacy/src/components/assessment/AssignItem/state/reducer';
import filters from 'legacy/src/components/assessment/Filters/state/reducer';
import itemTags from 'legacy-src/utility/ItemTags/state/reducer';
import submissionData from 'legacy-src/utility/withSubmissionData/reducer';
import { unitsReducer as units, unitsSaga } from 'legacy-packages/unit-outline';
import questionBySharedStimulus from './questionBySharedStimulus';
import assessmentDataCache from './assessmentData';
import activeItem from './activeItem';
import skillSummary from './skillSummary';
import filterControls from 'legacy-src/utility/Filters/reducers';
import authReducer from 'legacy/src/components/auth/reducer';
import courseOutline from './courseOutline';
import sharedResources from './sharedResources';
import resourceFilters from './resourceFilters';
import searchedResources from './searchedResources';
import apdCarouselData from './apdCarousel';
import teacherAssignment, {
  assignmentStudentsStatus,
} from './teacherAssignment';
import paperscorer from './paperscorer';
import impersonating, {
  sagaReducer as impersonatingSagaReducer,
} from 'legacy/src/components/impersonating/state/reducer';
import visitedSubunits from 'chameleon/ui-stack/ui/units/subunits-list/state/reducers';
import visitedUnits from 'chameleon-src/components/SidebarWrapperTwoLevels/state/reducers';
import bookmarks from './bookmarks';
import questionPreview from 'chameleon-src/components/QuestionPreviewWrapper/state/reducers.js';
import itemShareInvites from 'chameleon-src/reducers/itemShareInvites';
import itemShares from 'chameleon-src/reducers/itemShares';
import assessmentSummary from 'chameleon-src/reducers/assessmentSummary';

/* end */
import apstudentSaga from 'legacy/src/components/apstudent/handlers/state/sagas';
import assessmentSaga from 'legacy/src/components/assessment/state/sagas';
import assignItemSaga from 'legacy/src/components/assessment/AssignItem/state/sagas';
import itemTagsSaga from 'legacy-src/utility/ItemTags/state/sagas';
import submissionDataSaga from 'legacy-src/utility/withSubmissionData/sagas';
import authSaga from 'legacy/src/components/auth/sagas';
import paperscorerSaga from 'legacy/src/components/paperscorer/sagas';
import questionPreviewToggle from './questionPreviewToggle';
import featureFlag from './featureFlag';
import admin from './admin';
import nav from './nav';
import subject from './subject';
import userData from './me';
import classes from './classes';
import userSubjects from './userSubjects';
import currentState from './currentState';
import entrypointSaga from '../sagas';
import impersonatingSaga from 'legacy/src/components/impersonating/state/sagas';
import studentsBySection from './studentsBySection';
import secureAssignmentIds from './secureAssignmentIds';

function createRootReducer(apolloClient, history, initialState = {}) {
  // create the saga middleware
  const sagaMiddleware = createSagaMiddleware();

  // notice how we now only export the rootSaga
  // single entry point to start all Sagas at once
  function* rootSaga() {
    yield all([
      unitsSaga(),
      apstudentSaga(),
      assessmentSaga(),
      assignItemSaga(),
      routinePromiseWatcherSaga(),
      itemTagsSaga(),
      submissionDataSaga(),
      authSaga(),
      entrypointSaga(),
      paperscorerSaga(),
      impersonatingSaga(),
    ]);
  }

  const middleware = [
    apolloClient.middleware(),
    thunk,
    sagaMiddleware,
    routerMiddleware(history),
  ];

  const persistConfig = {
    key: 'ui-fym',
    storage,
    version: 1,
    whitelist: ['activeUnits', 'activeTabsLength'],
  };

  leanReducer.setGlobalScope('lean');
  const reducers = combineReducers({
    apollo: apolloClient.reducer(),
    form: formReducer,
    userData,
    currentState,
    admin,
    wizards,
    units: persistReducer(persistConfig, units),
    questionPreviewToggle,
    nav,
    subject,
    apstudent,
    assessment,
    assignItem,
    filters,
    classes,
    userSubjects,
    lean: leanReducer,
    featureFlag,
    itemTags,
    questionBySharedStimulus,
    assessmentDataCache,
    submissionData,
    activeItem,
    skillSummary,
    filterControls,
    courseOutline,
    apdCarouselData,
    auth: authReducer,
    sharedResources,
    resourceFilters,
    searchedResources,
    teacherAssignment,
    paperscorer,
    assignmentStudentsStatus,
    impersonating,
    impersonatingUser: impersonatingSagaReducer,
    visitedSubunits: persistReducer(
      {
        key: 'visited-subunits',
        storage,
        version: 1,
        stateReconciler: hardSet,
      },
      visitedSubunits,
    ),
    visitedUnits: persistReducer(
      {
        key: 'visited-units',
        storage,
        version: 1,
        stateReconciler: hardSet,
      },
      visitedUnits,
    ),
    studentsBySection,
    bookmarks,
    questionPreview,
    itemShareInvites,
    itemShares,
    assessmentSummary,
    secureAssignmentIds,
  });

  if (REDUX_LOGGING_ENABLED) {
    middleware.push(createLogger());
  }

  const store = createStore(
    reducers,
    initialState, // initial state
    compose(
      applyMiddleware(...middleware),
      // devToolsExtension setting
      typeof window.__REDUX_DEVTOOLS_EXTENSION__ !== 'undefined'
        ? window.__REDUX_DEVTOOLS_EXTENSION__({
            name: 'AP Classroom Chameleon',
          })
        : (f) => f,
    ),
  );

  // Run the saga
  sagaMiddleware.run(rootSaga);

  return store;
}

export default createRootReducer;
